import { useViewSuperblocksV2Context } from "contexts/ViewSuperblocksV2Context";

import {
  ApiTriggerType,
  orgHasFeature,
  OrgFeature,
} from "@superblocksteam/shared";

import { useListWorkflowsAndScheduledJobsQuery } from "store/slices/reduxApi/workflowsAndScheduledJobs";

/**
 * Hook to determine if scheduled jobs feature should be shown based on:
 * 1. Organization version (V1 orgs have scheduled jobs enabled, V2 orgs do not)
 * 2. Organization having existing scheduled jobs
 * 3. V2 preview mode (V1 orgs viewing as V2)
 *
 * @returns {
 *   canCreateScheduledJobs: boolean - Can create new scheduled jobs (V1 org only)
 *   hasExistingScheduledJobs: boolean - Organization has existing scheduled jobs
 *   shouldShowScheduledJobs: boolean - Should show scheduled jobs in UI (V1 org only)
 * }
 */
export function useScheduledJobsEnabled() {
  const { effectiveOrgVersion } = useViewSuperblocksV2Context();
  const orgVersion = effectiveOrgVersion;
  const scheduledJobsEnabledForOrg = orgHasFeature(
    orgVersion,
    OrgFeature.ScheduledJobs,
  );

  const { data: workflowsAndScheduledJobs = [] } =
    useListWorkflowsAndScheduledJobsQuery(undefined, {
      refetchOnMountOrArgChange: false,
    });

  const hasExistingScheduledJobs = workflowsAndScheduledJobs.some(
    (api) =>
      api?.triggerType === ApiTriggerType.SCHEDULE ||
      api?.actions?.triggerType === ApiTriggerType.SCHEDULE,
  );

  if (!scheduledJobsEnabledForOrg) {
    return {
      canCreateScheduledJobs: false,
      hasExistingScheduledJobs: false,
      shouldShowScheduledJobs: false,
    };
  }

  return {
    canCreateScheduledJobs: true,
    hasExistingScheduledJobs,
    shouldShowScheduledJobs: true,
  };
}
